import { z } from 'zod';

import { entityIdSchema, isoDateSchema, isoDateTimeSchema, pageMetaSchema } from './common.js';

export const EPI_REFERENCE_MAX_UTF16 = 120;
export const EPI_LABEL_MAX_UTF16 = 200;
export const EPI_TYPE_MAX_UTF16 = 100;
export const EPI_SHORT_TEXT_MAX_UTF16 = 160;
export const EPI_DESCRIPTION_MAX_UTF16 = 2_000;
export const EPI_CANONICAL_TEXT_MAX_EXPANSION_UTF16 = 18;
export const EPI_CANONICAL_REFERENCE_MAX_UTF16 =
  EPI_REFERENCE_MAX_UTF16 * EPI_CANONICAL_TEXT_MAX_EXPANSION_UTF16;

export const epiStatusSchema = z.enum([
  'in_service',
  'inspection_due',
  'out_of_service',
  'retired',
]);
export const epiAlertLevelSchema = z.enum(['OVERDUE', 'DUE_SOON', 'OUT_OF_SERVICE', 'NONE']);
export const epiLotStatusSchema = z.enum(['active', 'archived']);
export const epiControlTypeSchema = z.enum(['periodic', 'pre_use', 'post_incident', 'other']);
export const epiControlResultSchema = z.enum(['passed', 'failed', 'restricted']);
export const epiArchiveStateSchema = z.enum(['active', 'archived', 'all']);

export const epiReferenceSchema = z.string().trim().min(1).max(EPI_REFERENCE_MAX_UTF16);
export const epiLabelSchema = z.string().trim().min(1).max(EPI_LABEL_MAX_UTF16);
export const epiTypeSchema = z.string().trim().min(1).max(EPI_TYPE_MAX_UTF16);
export const epiOptionalTextSchema = z.string().trim().max(EPI_SHORT_TEXT_MAX_UTF16).nullable();
export const epiDescriptionSchema = z.string().trim().max(EPI_DESCRIPTION_MAX_UTF16);
export const epiCanonicalReferenceSchema = z.string().min(1).max(EPI_CANONICAL_REFERENCE_MAX_UTF16);

function addDateIssue(
  value: {
    readonly purchasedOn?: string | null | undefined;
    readonly commissionedOn?: string | null | undefined;
    readonly endOfLifeOn?: string | null | undefined;
  },
  context: z.RefinementCtx,
): void {
  const pairs = [
    [value.purchasedOn, value.commissionedOn, 'commissionedOn'],
    [value.purchasedOn, value.endOfLifeOn, 'endOfLifeOn'],
    [value.commissionedOn, value.endOfLifeOn, 'endOfLifeOn'],
  ] as const;
  for (const [before, after, path] of pairs) {
    if (
      before !== undefined &&
      before !== null &&
      after !== undefined &&
      after !== null &&
      before > after
    ) {
      context.addIssue({
        code: 'custom',
        path: [path],
        message: 'Les dates EPI ne sont pas cohérentes.',
      });
      return;
    }
  }
}

const epiItemFieldsSchema = z.object({
  reference: epiReferenceSchema,
  label: epiLabelSchema,
  type: epiTypeSchema,
  brand: epiOptionalTextSchema.default(null),
  model: epiOptionalTextSchema.default(null),
  size: epiOptionalTextSchema.default(null),
  serialNumber: epiOptionalTextSchema.default(null),
  status: epiStatusSchema.default('in_service'),
  purchasedOn: isoDateSchema.nullable().default(null),
  commissionedOn: isoDateSchema.nullable().default(null),
  endOfLifeOn: isoDateSchema.nullable().default(null),
  nextControlOn: isoDateSchema.nullable().default(null),
  lotId: entityIdSchema.nullable().default(null),
});

export const epiItemCreateInputSchema = epiItemFieldsSchema.strict().superRefine(addDateIssue);

export const epiItemUpdateInputSchema = epiItemFieldsSchema
  .partial()
  .extend({ revision: z.number().int().positive() })
  .strict()
  .superRefine(addDateIssue);

export const epiRevisionInputSchema = z.object({ revision: z.number().int().positive() }).strict();

export const epiLotCreateInputSchema = z
  .object({
    reference: epiReferenceSchema,
    label: epiLabelSchema,
    description: epiDescriptionSchema.default(''),
  })
  .strict();

export const epiLotUpdateInputSchema = z
  .object({
    reference: epiReferenceSchema.optional(),
    label: epiLabelSchema.optional(),
    description: epiDescriptionSchema.optional(),
    revision: z.number().int().positive(),
  })
  .strict();

export const epiControlCreateInputSchema = z
  .object({
    revision: z.number().int().positive(),
    controlledOn: isoDateSchema,
    type: epiControlTypeSchema,
    result: epiControlResultSchema,
    comment: epiDescriptionSchema.default(''),
    nextControlOn: isoDateSchema.nullable().default(null),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.nextControlOn !== null && value.nextControlOn < value.controlledOn) {
      context.addIssue({
        code: 'custom',
        path: ['nextControlOn'],
        message: 'Le prochain contrôle ne peut pas précéder le contrôle courant.',
      });
    }
  });

export const epiLotSummarySchema = z
  .object({
    id: entityIdSchema,
    reference: epiReferenceSchema,
    label: epiLabelSchema,
    status: epiLotStatusSchema,
  })
  .strict();

export const epiLotSchema = epiLotSummarySchema
  .extend({
    description: epiDescriptionSchema,
    revision: z.number().int().positive(),
    archivedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    createdBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: entityIdSchema.nullable(),
  })
  .strict();

export const epiItemSchema = epiItemFieldsSchema
  .omit({ lotId: true })
  .extend({
    id: entityIdSchema,
    lot: epiLotSummarySchema.nullable(),
    alertLevel: epiAlertLevelSchema,
    archived: z.boolean(),
    revision: z.number().int().positive(),
    createdAt: isoDateTimeSchema,
    createdBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: entityIdSchema.nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
  })
  .strict();

export const epiControlEventSchema = z
  .object({
    id: entityIdSchema,
    itemId: entityIdSchema,
    controlledOn: isoDateSchema,
    type: epiControlTypeSchema,
    result: epiControlResultSchema,
    comment: epiDescriptionSchema,
    nextControlOn: isoDateSchema.nullable(),
    resultingStatus: epiStatusSchema,
    authorId: entityIdSchema.nullable(),
    createdAt: isoDateTimeSchema,
  })
  .strict();

const epiItemCursorPayloadSchema = z
  .object({ normalizedReference: epiCanonicalReferenceSchema, id: entityIdSchema })
  .strict();

export type EpiItemCursor = z.infer<typeof epiItemCursorPayloadSchema>;

export function encodeEpiItemCursor(cursor: EpiItemCursor): string {
  const payload = epiItemCursorPayloadSchema.parse(cursor);
  return `e1.${encodeURIComponent(JSON.stringify(payload))}`;
}

export function decodeEpiItemCursor(cursor: string): EpiItemCursor | null {
  if (!cursor.startsWith('e1.')) return null;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(cursor.slice(3)));
    const result = epiItemCursorPayloadSchema.safeParse(parsed);
    return result.success && encodeEpiItemCursor(result.data) === cursor ? result.data : null;
  } catch {
    return null;
  }
}

export const epiItemCursorSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => decodeEpiItemCursor(value) !== null, 'Curseur EPI invalide.');

export const epiItemListQuerySchema = z
  .object({
    cursor: epiItemCursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(200).optional(),
    type: epiTypeSchema.optional(),
    status: epiStatusSchema.optional(),
    lotId: entityIdSchema.optional(),
    alert: epiAlertLevelSchema.optional(),
    archiveState: epiArchiveStateSchema.default('active'),
  })
  .strict();

export const epiLotListQuerySchema = z
  .object({
    cursor: epiItemCursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(100),
    search: z.string().trim().max(200).optional(),
    archiveState: epiArchiveStateSchema.default('active'),
  })
  .strict();

const epiControlCursorPayloadSchema = z
  .object({ controlledOn: isoDateSchema, id: entityIdSchema })
  .strict();

export type EpiControlCursor = z.infer<typeof epiControlCursorPayloadSchema>;

export function encodeEpiControlCursor(cursor: EpiControlCursor): string {
  const payload = epiControlCursorPayloadSchema.parse(cursor);
  return `ec1.${encodeURIComponent(JSON.stringify(payload))}`;
}

export function decodeEpiControlCursor(cursor: string): EpiControlCursor | null {
  if (!cursor.startsWith('ec1.')) return null;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(cursor.slice(4)));
    const result = epiControlCursorPayloadSchema.safeParse(parsed);
    return result.success && encodeEpiControlCursor(result.data) === cursor ? result.data : null;
  } catch {
    return null;
  }
}

export const epiControlListQuerySchema = z
  .object({
    cursor: z
      .string()
      .trim()
      .min(1)
      .refine((value) => decodeEpiControlCursor(value) !== null, 'Curseur contrôle EPI invalide.')
      .optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();

export const epiCountersSchema = z
  .object({
    filtered: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
    dueSoon: z.number().int().nonnegative(),
    outOfService: z.number().int().nonnegative(),
  })
  .strict();

export const epiItemPageSchema = z
  .object({
    items: z.array(epiItemSchema),
    page: pageMetaSchema,
    counters: epiCountersSchema,
    calculatedOn: isoDateSchema,
  })
  .strict();

export const epiLotPageSchema = z
  .object({ items: z.array(epiLotSchema), page: pageMetaSchema })
  .strict();

export const epiControlPageSchema = z
  .object({ items: z.array(epiControlEventSchema), page: pageMetaSchema })
  .strict();

export type EpiStatus = z.infer<typeof epiStatusSchema>;
export type EpiAlertLevel = z.infer<typeof epiAlertLevelSchema>;
export type EpiLotStatus = z.infer<typeof epiLotStatusSchema>;
export type EpiControlResult = z.infer<typeof epiControlResultSchema>;
export type EpiControlType = z.infer<typeof epiControlTypeSchema>;
export type EpiItemCreateInput = z.infer<typeof epiItemCreateInputSchema>;
export type EpiItemUpdateInput = z.infer<typeof epiItemUpdateInputSchema>;
export type EpiItem = z.infer<typeof epiItemSchema>;
export type EpiItemListQuery = z.infer<typeof epiItemListQuerySchema>;
export type EpiLotCreateInput = z.infer<typeof epiLotCreateInputSchema>;
export type EpiLotUpdateInput = z.infer<typeof epiLotUpdateInputSchema>;
export type EpiLot = z.infer<typeof epiLotSchema>;
export type EpiLotListQuery = z.infer<typeof epiLotListQuerySchema>;
export type EpiControlCreateInput = z.infer<typeof epiControlCreateInputSchema>;
export type EpiControlEvent = z.infer<typeof epiControlEventSchema>;
export type EpiControlListQuery = z.infer<typeof epiControlListQuerySchema>;
